"use client";

import Image from "next/image";
import { SimpleColumn } from "../types";
import { renderEmpty, renderRelativeTime, renderEmailLink } from "./shared";
import { Badge } from "@/components/ui/badge";
import { SmartContactDisplay } from "@/components/shared/smart-display/SmartContactDisplay";
import type { User } from "@/lib/types/user";

/** Optional joined relations the admin user table flattens onto a row for display. */
interface UserJoinedRelations {
  contact?: { id?: string; name?: string | null; company_name?: string | null; email?: string | null } | null;
  client?: { id?: string; name?: string | null; company_name?: string | null; email?: string | null } | null;
}

/** Row shape rendered by the admin user table — codegen-derived `User` plus display joins. */
type UserColumnRow = User & UserJoinedRelations;

const ACCESS_LEVEL_COLORS: Record<string, string> = {
  read: "bg-blue-100 text-blue-800",
  write: "bg-green-100 text-green-800",
  admin: "bg-red-100 text-red-800",
};

export function createUserColumns(_users: UserColumnRow[]): SimpleColumn[] {
  const columns: SimpleColumn[] = [];

  columns.push({
    id: "name",
    label: "Name",
    category: "basic",
    visible: true,
    priority: 1,
    width: 200,
    render: (_value: unknown, item: UserColumnRow) => (
      <div className="min-w-0">
        <div className="font-medium text-card-foreground truncate">
          {item.first_name} {item.last_name}
        </div>
        <div className="text-sm text-gray-500 truncate">{item.email}</div>
      </div>
    ),
  });

  columns.push({
    id: "email",
    label: "Email",
    category: "contact",
    visible: true,
    priority: 2,
    width: 200,
    render: (_value: unknown, item: UserColumnRow) =>
      renderEmailLink(item.email ?? "", {
        item: item as unknown as Record<string, unknown>,
      }),
  });

  columns.push({
    id: "role",
    label: "Role",
    category: "status",
    visible: true,
    priority: 3,
    width: 100,
    render: (_value: unknown, item: UserColumnRow) => (
      <Badge
        variant={
          item.role === "admin" ? "default" : item.role === "employee" ? "secondary" : "outline"
        }
        className="text-xs"
      >
        {item.role}
      </Badge>
    ),
  });

  columns.push({
    id: "is_active",
    label: "Status",
    category: "status",
    visible: true,
    priority: 4,
    width: 100,
    render: (_value: unknown, item: UserColumnRow) => (
      <Badge variant={item.is_active ? "default" : "secondary"} className="text-xs">
        {item.is_active ? "Active" : "Inactive"}
      </Badge>
    ),
  });

  columns.push({
    id: "first_name",
    label: "First Name",
    category: "basic",
    visible: false,
    priority: 5,
    width: 120,
  });

  columns.push({
    id: "last_name",
    label: "Last Name",
    category: "basic",
    visible: false,
    priority: 6,
    width: 120,
  });

  columns.push({
    id: "role_id",
    label: "Role ID",
    category: "basic",
    visible: false,
    priority: 7,
    width: 120,
    render: (_value: unknown, item: UserColumnRow) => {
      if (!item.role_id) return renderEmpty();
      return <span className="font-mono text-gray-600 text-sm">{item.role_id}</span>;
    },
  });

  columns.push({
    id: "cognito_user_id",
    label: "Cognito ID",
    category: "basic",
    visible: false,
    priority: 8,
    width: 150,
    render: (_value: unknown, item: UserColumnRow) => {
      if (!item.cognito_user_id) return renderEmpty();
      return <span className="font-mono text-gray-600 text-sm">{item.cognito_user_id}</span>;
    },
  });

  columns.push({
    id: "contact_id",
    label: "Contact ID",
    category: "basic",
    visible: false,
    priority: 9,
    width: 120,
    render: (_value: unknown, item: UserColumnRow) => (
      <SmartContactDisplay
        contactId={item.contact_id ?? ""}
        contactData={item.contact}
        fallbackText="-"
        showPopover={false}
      />
    ),
  });

  columns.push({
    id: "client_id",
    label: "Client ID",
    category: "basic",
    visible: false,
    priority: 10,
    width: 120,
    render: (_value: unknown, item: UserColumnRow) => (
      <SmartContactDisplay
        contactId={item.client_id ?? ""}
        contactData={item.client}
        fallbackText="-"
        showPopover={false}
      />
    ),
  });

  columns.push({
    id: "accessLevel",
    label: "Access Level",
    category: "status",
    visible: false,
    priority: 11,
    width: 120,
    render: (_value: unknown, item: UserColumnRow) => {
      if (!item.accessLevel) return renderEmpty();
      return (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            ACCESS_LEVEL_COLORS[item.accessLevel] || "bg-card-100 text-gray-800"
          }`}
        >
          {item.accessLevel}
        </span>
      );
    },
  });

  columns.push({
    id: "userType",
    label: "User Type",
    category: "status",
    visible: false,
    priority: 12,
    width: 120,
    render: (_value: unknown, item: UserColumnRow) => {
      if (!item.userType) return renderEmpty();
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          {item.userType}
        </span>
      );
    },
  });

  columns.push({
    id: "image",
    label: "Avatar",
    category: "basic",
    visible: false,
    priority: 13,
    width: 100,
    render: (_value: unknown, item: UserColumnRow) => {
      if (!item.image) return renderEmpty();
      return (
        <Image
          src={item.image}
          alt="Avatar"
          width={32}
          height={32}
          className="w-8 h-8 rounded-full object-cover"
        />
      );
    },
  });

  columns.push({
    id: "email_verified",
    label: "Email Verified",
    category: "status",
    visible: false,
    priority: 14,
    width: 120,
    render: (_value: unknown, item: UserColumnRow) => (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          item.email_verified ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
        }`}
      >
        {item.email_verified ? "Verified" : "Unverified"}
      </span>
    ),
  });

  columns.push({
    id: "provider",
    label: "Provider",
    category: "basic",
    visible: false,
    priority: 15,
    width: 100,
    render: (_value: unknown, item: UserColumnRow) => {
      if (!item.provider) return renderEmpty();
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
          {item.provider}
        </span>
      );
    },
  });

  columns.push({
    id: "sub",
    label: "Subject",
    category: "basic",
    visible: false,
    priority: 16,
    width: 150,
    render: (_value: unknown, item: UserColumnRow) => {
      if (!item.sub) return renderEmpty();
      return <span className="font-mono text-gray-600 text-sm">{item.sub}</span>;
    },
  });

  columns.push({
    id: "roles",
    label: "Roles",
    category: "basic",
    visible: false,
    priority: 17,
    width: 200,
    render: (_value: unknown, item: UserColumnRow) => {
      if (!item.roles) return renderEmpty();
      return (
        <div className="flex flex-wrap gap-1">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {item.roles.name || item.roles.display_name}
          </span>
        </div>
      );
    },
  });

  columns.push({
    id: "created_at",
    label: "Created",
    category: "dates",
    visible: false,
    priority: 18,
    width: 120,
    render: (value: string | null | undefined) => renderRelativeTime(value),
  });

  columns.push({
    id: "updated_at",
    label: "Updated",
    category: "dates",
    visible: false,
    priority: 19,
    width: 120,
    render: (value: string | null | undefined) => renderRelativeTime(value),
  });

  columns.push({
    id: "available_hours",
    label: "Available Hours",
    category: "status",
    visible: true,
    priority: 5,
    width: 130,
    render: (_value: unknown, item: UserColumnRow) => {
      const hours = item.available_hours ?? 0;
      return (
        <div className="flex items-center gap-2">
          <span className="font-medium text-card-foreground">{hours}</span>
          <span className="text-xs text-gray-500">hrs/week</span>
        </div>
      );
    },
  });

  columns.push({
    id: "is_billable",
    label: "Billable",
    category: "status",
    visible: false,
    priority: 20,
    width: 100,
    render: (_value: unknown, item: UserColumnRow) => (
      <Badge variant={item.is_billable ? "default" : "secondary"} className="text-xs">
        {item.is_billable ? "Billable" : "Non-Billable"}
      </Badge>
    ),
  });

  return columns.sort((a, b) => a.priority - b.priority);
}
